import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import api from "../../services/api.js";

export default function AddBulkTransactionModal({
  open,
  onClose,
  token,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  /* ================= DOWNLOAD TEMPLATE ================= */
  const downloadTemplate = () => {
    const data = [
      {
        customer_id: "",
        amountin: "",
        transid: "",
        paymethodid: "",
        datepaid: "",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "BulkTransaction");

    XLSX.writeFile(workbook, "bulk_transaction_template.xlsx");
  };

  /* ================= FILE PREVIEW ================= */
  const handleFileChange = async (e) => {
    const uploadedFile = e.target.files[0];
    setError(null);
    setResult(null);

    if (!uploadedFile) return;

    setFile(uploadedFile);

    try {
      const buffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "buffer" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
      });

      if (!rows.length) {
        throw new Error("File kosong atau format tidak valid");
      }

      const requiredColumns = ["customer_id", "amountin", "transid"];

      for (const col of requiredColumns) {
        if (!Object.keys(rows[0]).includes(col)) {
          throw new Error(`Kolom wajib "${col}" tidak ditemukan`);
        }
      }

      setPreview(rows.slice(0, 10));
    } catch (err) {
      setFile(null);
      setPreview([]);
      setError(err.message);
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("File belum dipilih");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post("/transaction/bulk", formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(res.data);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memproses bulk transaksi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-4xl rounded-xl bg-white dark:bg-gray-900 p-6 shadow-lg overflow-auto max-h-[90vh]"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Add Bulk Transaction
            </h2>

            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-500"
            >
              ✕
            </button>
          </div>

          {/* Actions */}
          <div className="flex justify-between mb-4">
            <button
              onClick={downloadTemplate}
              className="text-sm rounded-md bg-green-600 px-3 py-2 text-white hover:bg-green-700"
            >
              Download Template Excel
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-700 dark:text-gray-200
              file:mr-4 file:rounded-md file:border-0
              file:bg-blue-600 file:px-4 file:py-2
              file:text-white hover:file:bg-blue-700"
            />

            {/* Preview */}
            {preview.length > 0 && (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      {Object.keys(preview[0]).map((key) => (
                        <th
                          key={key}
                          className="px-2 py-1 text-gray-700 dark:text-gray-200"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-t dark:border-gray-700">
                        {Object.values(row).map((val, idx) => (
                          <td
                            key={idx}
                            className="px-2 py-1 text-gray-600 dark:text-gray-300"
                          >
                            {String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="p-2 text-xs text-gray-500">
                  Preview maksimal 10 baris
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-md bg-red-100 dark:bg-red-900/30 p-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="rounded-md bg-green-100 dark:bg-green-900/30 p-3 text-sm text-green-700 dark:text-green-300">
                <p className="font-semibold">{result.message}</p>
                <p>Total: {result.summary.total}</p>
                <p>Success: {result.summary.success}</p>
                <p>Failed: {result.summary.failed}</p>
              </div>
            )}
            {result && result.data && (
              <>
                {result.data.success.length > 0 && (
                  <div>
                    <p className="font-semibold">Detail Success:</p>
                    <ul className="list-disc ml-5">
                      {result.data.success.map((item, idx) => (
                        <li key={idx}>
                          Row {item.row} | TransID: {item.transid} | Invoice
                          Status: {item.invoice_status}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.data.failed.length > 0 && (
                  <div>
                    <p className="font-semibold">Detail Failed:</p>
                    <ul className="list-disc ml-5 text-red-600">
                      {result.data.failed.map((item, idx) => (
                        <li key={idx}>
                          Row {item.row} | Customer: {item.customer_id ?? "-"} |
                          Trans-ID: {item.transid ?? "-"} | Error: {item.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-4 py-2 bg-gray-200 dark:bg-gray-700"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-md px-4 py-2 bg-blue-600 text-white disabled:opacity-50"
              >
                {loading ? "Processing..." : "Upload"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
